import { FC, useState, useRef, useEffect } from "react";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import { toastOnce } from "../utils/toastOnce";
import { WeekData } from "../types/rentalCarDatesRangeInput.types";
import { getWeeksInMonth, weekContainsUnavailableDates, getWeekIntervalDays, weekKey, weekKeysBetween, weekStartingOn } from "../utils/weeksHelper";
import { useData } from "data/data-provider";

interface WeeksViewProps {
  unavailableDates?: Date[];
  partialUnavailability?: { [key: string]: { from: string; to: string }[] };
  /** 1NOW-11176: Design B's plain version (hairline cells, no icons). */
  look?: "b";
}

const WeeksView: FC<WeeksViewProps> = ({ 
  unavailableDates = [],
  partialUnavailability = {},
  look,
}) => {
  const b = look === "b";
  const { selectedWeeks, setSelectedWeeks, weeksViewMonth, setWeeksViewMonth, startTime, endTime, s_Time, e_Time } =
    useData();

  // The times the trip will carry decide how long a week runs (6 days or 7), and nothing else about it.
  const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
  const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
  const intervalDays = getWeekIntervalDays(currentStartTime, currentEndTime);
  const weeks = getWeeksInMonth(weeksViewMonth, unavailableDates, intervalDays);
  const currentMonthName = moment(weeksViewMonth).format("MMMM YYYY");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // A week is selected by the day it starts (weekKey), and that is the day its card shows: a click changes no
  // label, and the trip Save writes is the label (getSelectedWeeksDateRange). 1NOW-11212.
  const handleWeekSelect = (week: WeekData) => {
    if (week.isPast) return;
    const hasUnavailableDates = weekContainsUnavailableDates(
      week,
      unavailableDates,
      partialUnavailability,
      false // Don't allow partial availability
    );
    if (hasUnavailableDates) return;
    const key = weekKey(week);
    const sorted = [...selectedWeeks].sort();

    if (sorted.includes(key)) {
      // Only the first or the last week can come out; one in the middle would split the chain.
      if (key === sorted[0] || key === sorted[sorted.length - 1]) {
        setSelectedWeeks(sorted.filter((k) => k !== key));
      } else {
        toastOnce("You need to unselect the future week(s) first to remove this week from your booking, as this is a long-term booking chain.");
      }
      return;
    }

    // The weeks in between come with it, so the booking has no gap. Each has to be free (the first may be
    // limited on its first day, which is today).
    const withKey = [...sorted, key].sort();
    const chain = weekKeysBetween(withKey[0], withKey[withKey.length - 1]);
    const blocked = chain.some((k, i) =>
      weekContainsUnavailableDates(
        weekStartingOn(k, intervalDays, { isCurrentWeek: i === 0 }),
        unavailableDates,
        partialUnavailability,
        false
      )
    );
    if (blocked) {
      toastOnce("Cannot select weeks with unavailable dates in between. Please select consecutive available weeks.");
      return;
    }
    setSelectedWeeks(chain);
  };

  const currentMonth = moment().month(); 
  const currentYear = moment().year();
  const monthsInCurrentYear = 12 - currentMonth; // Remaining months in current year
  const availableMonths = Array.from({ length: monthsInCurrentYear }, (_, i) => {
    return moment().add(i, "months").toDate();
  });

  return (
    <div className="flex flex-col items-center">

      {/* Month Selector */}
      <div className="flex items-center space-x-2 mb-6">
        <button
          onClick={(e) => {
            e.preventDefault();
            const newMonth = moment(weeksViewMonth).subtract(1, "month");
            if (newMonth.isSameOrAfter(moment(), "month")) {
              setWeeksViewMonth(newMonth.toDate());
            }
          }}
          className="p-1 hover:bg-secondary-100 rounded-full disabled:opacity-50"
          disabled={moment(weeksViewMonth).isSame(moment(), "month")}
        >
          <ChevronLeftIcon className="w-4 h-4 text-secondary-text-color" />
        </button>
        
        {/* Custom Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsDropdownOpen(!isDropdownOpen);
            }}
            className={`px-4 py-2 border text-sm font-medium text-text-color bg-card-color focus:outline-none focus:ring-2 focus:ring-primary-brand cursor-pointer flex items-center text-center space-x-2 min-w-[160px] justify-center ${b ? "rounded-lg border-text-color/20 hover:border-text-color/40" : "border-secondary-100 rounded-full hover:bg-secondary-100"}`}
          >
            <span className="text-center">{currentMonthName}</span>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-full min-w-[200px] bg-card-color border border-secondary-100 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              {availableMonths.map((month) => {
                const monthValue = moment(month).format("YYYY-MM");
                const isSelected = monthValue === moment(weeksViewMonth).format("YYYY-MM");
                return (
                  <button
                    key={monthValue}
                    onClick={(e) => {
                      e.preventDefault();
                      const [year, m] = monthValue.split("-");
                      setWeeksViewMonth(new Date(parseInt(year), parseInt(m) - 1, 1));
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-secondary-100 transition-colors ${
                      isSelected ? 'bg-primary-6000 font-semibold text-on-primary' : 'text-text-color'
                    }`}
                  >
                    {moment(month).format("MMMM YYYY")}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        <button
          onClick={(e) => {
            e.preventDefault();
            setWeeksViewMonth(moment(weeksViewMonth).add(1, "month").toDate());
          }}
          className="p-1 hover:bg-secondary-100 rounded-full"
        >
          <ChevronRightIcon className="w-4 h-4 text-secondary-text-color" />
        </button>
      </div>

      <div className={b ? "grid w-full grid-cols-2 gap-2 sm:grid-cols-4" : "grid grid-cols-2 sm:grid-cols-4 gap-4"}>
        {weeks.map((week) => {
          // The card shows the week the chain gives it (getWeeksInMonth), selectable or not.
          const key = weekKey(week);
          const isSelected = selectedWeeks.includes(key);
          const hasUnavailableDates = weekContainsUnavailableDates(
            week,
            unavailableDates,
            partialUnavailability,
            false // Don't allow partial availability
          );
          const isDisabled = week.isPast || hasUnavailableDates;

          return (
            <button
              key={key}
              onClick={(e) => {
                e.preventDefault();
                handleWeekSelect(week);
              }}
              disabled={isDisabled}
              className={`relative flex flex-col transition-all ${
                b ? "items-start rounded-lg border px-3 py-2.5 text-left" : "items-center p-4 rounded-2xl border-2"
              } ${
                b
                  ? isDisabled
                    ? "border-text-color/10 opacity-50 cursor-not-allowed"
                    : isSelected
                      ? "border-primary-brand bg-primary-brand/10"
                      : "border-text-color/20 hover:border-text-color/40"
                  : isDisabled
                    ? "border-secondary-100 bg-secondary-100/40 opacity-50 cursor-not-allowed"
                    : isSelected
                      ? "border-primary-6000 bg-secondary-100"
                      : "border-secondary-100 hover:border-primary-brand"
              }`}
            >
              {isSelected && !b && (
                <div className="absolute top-1 right-2 z-10 bg-primary-6000 text-on-primary px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
               
                </div>
              )}
              <div className={b ? "hidden" : "w-12 h-12 flex items-center justify-center mb-2"}>
                <CalendarIcon
                  className={`w-8 h-8 ${
                    isDisabled ? "text-secondary-text-color/50" : "text-secondary-text-color"
                  }`}
                />
              </div>
              <span
                className={`text-sm font-semibold ${
                  isDisabled ? "text-secondary-text-color" : "text-text-color"
                }`}
              >
                Week {week.weekNum}
              </span>
              <span
                className={`text-xs mt-1 ${
                  isDisabled ? "text-secondary-text-color/60" : "text-secondary-text-color"
                }`}
              >
                {moment(week.start).format("MMM D")} -{" "}
                {moment(week.end).format("MMM D")}
              </span>
              {hasUnavailableDates && !week.isPast && (
                <span className={`text-[10px] mt-1 ${b ? "text-text-color/60" : "text-red-500"}`}>
                  Unavailable
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WeeksView;
